import React from 'react';

import { css, keyframes } from '@emotion/react';
import Box from '@mui/material/Box';

import { useLocale } from '@shared/hooks/locale';

type Props = {
  size?: number;
  label?: React.ReactNode;
  className?: string;
};

const compassSpin = keyframes`
  0%   { transform: rotate(0deg) scale(1); }
  75%  { transform: rotate(720deg) scale(1); }
  85%  { transform: rotate(720deg) scale(0.92); }
  100% { transform: rotate(720deg) scale(1); }
`;

const needleStyle = css`
  transform-box: view-box;
  transform-origin: 70px 70px;
  animation: ${compassSpin} 4s cubic-bezier(0.68, -0.6, 0.32, 1.6) infinite;
`;

export const CompassLoader = ({
  size = 140,
  label,
  className,
}: Props): React.ReactElement => {
  const { t } = useLocale();
  const gradientId = 'ra-compass-gradient';

  return (
    <div
      className={className}
      role="status"
      aria-label={t('Loading...')}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 140 140"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMid meet"
        style={{ filter: 'drop-shadow(0 8px 24px rgba(10, 57, 183, 0.25))' }}
      >
        <defs>
          <linearGradient
            id={gradientId}
            x1="20.503"
            y1="119.498"
            x2="119.497"
            y2="20.503"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#54BEDA" />
            <stop offset="1" stopColor="#0A39B7" />
          </linearGradient>
        </defs>

        <path
          d="M0 70C0 108.66 31.34 140 70 140C108.66 140 140 108.66 140 70C140 51.44 132.62 33.63 119.5 20.5C106.368 7.37741 88.5646 0.00414909 70 0C31.34 0 0 31.34 0 70Z"
          fill={`url(#${gradientId})`}
        />

        <path
          css={needleStyle}
          d="M50.09 56.82C50.5566 55.2261 51.4166 53.7753 52.5909 52.6009C53.7653 51.4266 55.2161 50.5666 56.81 50.1L94.1 39.13Q103.69 36.31 100.87 45.9L89.9 83.18C89.4334 84.7739 88.5734 86.2247 87.3991 87.3991C86.2247 88.5734 84.7739 89.4334 83.18 89.9L45.9 100.87Q36.31 103.69 39.13 94.1L50.09 56.82Z"
          fill="white"
        />
      </svg>

      {label != null && (
        <Box
          sx={theme => ({
            marginTop: '30px',
            color: '#64748b',
            fontSize: 14,
            fontWeight: 500,
            ...theme.applyStyles('dark', {
              color: '#94a3b8',
            }),
          })}
        >
          {label}
        </Box>
      )}
    </div>
  );
};
